import React, { ChangeEvent, FormEvent } from 'react';
import { Typography, makeStyles, TextField, Grid, Button, InputLabel, Theme } from '@material-ui/core';
import { useAppState } from '../../../state';
import { useTranslation } from 'react-i18next';
import Lang from '../../lang';

const useStyles = makeStyles((theme: Theme) => ({
  gutterBottom: {
    marginBottom: '1em',
  },
  inputContainer: {
    display: 'flex',
    justifyContent: 'space-between',
    margin: '1.5em 0 3.5em',
    '& div:not(:last-child)': {
      marginRight: '1em',
    },
    [theme.breakpoints.down('sm')]: {
      margin: '1.5em 0 2em',
    },
  },
  textFieldContainer: {
    width: '100%',
  },
  continueButton: {
    backgroundColor: '#212529',
    [theme.breakpoints.down('sm')]: {
      width: '100%',
    },
  },
  langContainer: {
    position: 'fixed',
    top: '2rem',
    right: '2rem',
  },
}));

interface RoomNameScreenProps {
  name: string;
  roomName: string;
  setName: (name: string) => void;
  setRoomName: (roomName: string) => void;
  handleSubmit: (event?: FormEvent<HTMLFormElement>) => void;
}

export default function RoomNameScreen({ name, roomName, setName, setRoomName, handleSubmit }: RoomNameScreenProps) {
  const classes = useStyles();
  const { user } = useAppState();
  const { t } = useTranslation();

  const handleNameChange = (event: ChangeEvent<HTMLInputElement>) => {
    setName(event.target.value);
  };

  const handleRoomNameChange = (event: ChangeEvent<HTMLInputElement>) => {
    setRoomName(event.target.value);
  };

  const hasUsername = !window.location.search.includes('customIdentity=true') && user?.displayName;

  const urlParams = new URLSearchParams(window.location.search);
  const userName = urlParams.get('identity');
  const roomId = urlParams.get('roomName');

  if (userName !== null && roomId !== null) {
    setName(userName);
    setRoomName(roomId);
    handleSubmit();
  }

  return (
    <>
      <div className={classes.langContainer}>
        <Lang />
      </div>
      <Typography variant="h5" className={classes.gutterBottom}>
        {t('common.join-room-title-text')}
      </Typography>
      <Typography variant="body1">
        {hasUsername ? t('common.join-room-body-text') : t('common.join-room-with-name-body-text')}
      </Typography>

      <form onSubmit={handleSubmit}>
        <div className={classes.inputContainer}>
          {!hasUsername && (
            <div className={classes.textFieldContainer}>
              <InputLabel shrink htmlFor="input-user-name">
                {t('common.name-input-text')}
              </InputLabel>
              <TextField
                id="input-user-name"
                variant="outlined"
                fullWidth
                size="small"
                value={name}
                onChange={handleNameChange}
              />
            </div>
          )}
          <div className={classes.textFieldContainer}>
            <InputLabel shrink htmlFor="input-room-name">
              {t('common.room-input-text')}
            </InputLabel>
            <TextField
              autoCapitalize="false"
              id="input-room-name"
              variant="outlined"
              fullWidth
              size="small"
              value={roomName}
              onChange={handleRoomNameChange}
            />
          </div>
        </div>
        <Grid container justify="flex-end">
          <Button
            variant="contained"
            type="submit"
            color="primary"
            disabled={!name || !roomName}
            className={classes.continueButton}
          >
            {t('common.continue-button-text')}
          </Button>
        </Grid>
      </form>
    </>
  );
}
